// Service adapter for the shared Patient Quick Actions component.
//
// The same UI is mounted on three surfaces which key contact-actions
// differently on the backend:
//   - 'mentor-session'  -> /api/secure/mentor/contact-actions   (bookedEventId)
//   - 'mentor-patient'  -> /api/secure/mentor/contact-actions   (patientId + clinic)
//   - 'support-patient' -> /api/secure/cc/support/contact-actions (patientId + clinic)
//
// This module normalizes those into a single API so the component never has to
// branch on `source`. Only the actions understood in Phase 0 (capabilities,
// send-attachment, conversation-opener) are implemented here; the remaining
// business actions get their own service functions in later phases.

import {
  createMentorReceiptByContext,
  fetchMentorReceiptPdfBlobUrl,
  getMentorContactCapabilities,
  getMentorDeliverySummaryByContext,
  getMentorPrescriptionsByContext,
  sendMentorGoogleReviewByContext,
  sendMentorPaymentLinkByContext,
  sendMentorPrescriptionByContext,
  sendMentorReceipt,
  sendMentorWhatsappAttachmentByContext,
  sendMentorWhatsappTemplateByContext,
} from 'src/mentor/services/practitionerContactService'
import {
  getSupportAgentContactCapabilities,
  sendSupportAgentGoogleReview,
  sendSupportAgentWhatsappAttachment,
  sendSupportAgentWhatsappIntro,
} from 'src/supportPortal/services/supportAgentContactService'
import {
  createSupportPatientReceipt,
  fetchSupportPatientReceiptPdfBlobUrl,
  sendSupportPatientPaymentLink,
  sendSupportPatientReceipt,
} from 'src/supportPortal/services/supportBookedEventActionsService'

export const QUICK_ACTION_SOURCES = {
  MENTOR_SESSION: 'mentor-session',
  MENTOR_PATIENT: 'mentor-patient',
  SUPPORT_PATIENT: 'support-patient',
}

export function isSupportSource(context) {
  return context?.source === QUICK_ACTION_SOURCES.SUPPORT_PATIENT
}

// Builds the payload the backend service functions expect. Mentor endpoints
// prefer bookedEventId when present and otherwise use the patient keys; support
// endpoints always take the patient keys (+ targetRole).
function toContextPayload(context = {}) {
  const base = {
    bookedEventId: context?.bookedEventId || undefined,
    patientId: context?.patientId || undefined,
    clinicBrandId: context?.clinicBrandId || undefined,
    clinicId: context?.clinicId || undefined,
    clinicBrandName: context?.clinicBrandName || undefined,
  }
  if (isSupportSource(context)) {
    return { ...base, targetRole: 'patient' }
  }
  return base
}

// Normalizes the (slightly different) mentor/support capability responses into
// one shape the component reads.
function normalizeCapabilities(result) {
  if (!result?.ok) {
    return { ok: false, message: result?.message || 'Unable to load contact capabilities.' }
  }
  const whatsapp = result?.channels?.whatsapp || {}
  const email = result?.channels?.email || {}
  const data = result?.data || {}
  return {
    ok: true,
    whatsappEnabled: !!whatsapp.enabled,
    whatsappReason: whatsapp.reason || '',
    emailEnabled: !!email.enabled,
    emailReason: email.reason || '',
    // A channel is deliverable if either WhatsApp or email can go out.
    anyChannelEnabled: !!whatsapp.enabled || !!email.enabled,
    googleReviewConfigured: !!result?.googleReviewConfigured,
    patientHasAccount: !!result?.patientHasAccount,
    warmWindowOpen: !!whatsapp.warmWindowOpen,
    conversationStarterConfigured: !!whatsapp.conversationStarterConfigured,
    needsTemplateForFreeForm: whatsapp.needsTemplateForFreeForm ?? !whatsapp.warmWindowOpen,
    targetDisplayName: result?.targetDisplayName || '',
    targetWhatsappNumber: result?.targetWhatsappNumber || '',
    // Masked destination summary (mentor sources only) — where the sends will actually go.
    // contactDetailsVisible=false means the clinic hides patient contact details from this
    // practitioner; the *OnFile flags stay policy-free so the UI can say "on file but hidden".
    // contactSummarySupported feature-detects a backend that returns these fields, so the UI
    // stays silent (instead of wrongly claiming "no contact on file") against an older server.
    contactSummarySupported:
      data?.patientWhatsappAvailable !== undefined || data?.patientEmailAvailable !== undefined,
    contactDetailsVisible: data?.patientContactDetailsVisible !== false,
    whatsappNumberMasked: data?.patientWhatsappNumberMasked || '',
    emailMasked: data?.patientEmailMasked || '',
    whatsappOnFile: !!data?.patientWhatsappAvailable,
    emailOnFile: !!data?.patientEmailAvailable,
    raw: result,
  }
}

export async function loadQuickActionCapabilities(context) {
  const payload = toContextPayload(context)
  const result = isSupportSource(context)
    ? await getSupportAgentContactCapabilities(payload)
    : await getMentorContactCapabilities(payload)
  return normalizeCapabilities(result)
}

// Live delivery status of the recent WhatsApp sends to this patient (mentor sources only).
export async function loadQuickActionDeliverySummary(context, options = {}) {
  if (isSupportSource(context)) {
    return { ok: false, deliveries: [] }
  }
  return getMentorDeliverySummaryByContext(toContextPayload(context), options)
}

export async function sendQuickActionAttachment(context, caption, files) {
  const payload = toContextPayload(context)
  return isSupportSource(context)
    ? sendSupportAgentWhatsappAttachment(payload, caption, files)
    : sendMentorWhatsappAttachmentByContext(payload, caption, files)
}

// Sends the approved conversation opener so the patient can reply and unlock the
// 24-hour free-form window that attachments require.
export async function sendQuickActionConversationOpener(context) {
  const payload = toContextPayload(context)
  return isSupportSource(context)
    ? sendSupportAgentWhatsappIntro(payload)
    : sendMentorWhatsappTemplateByContext(payload, 'permission-template')
}

// Generates + sends the patient's prescription over WhatsApp/email (link or
// attachment per clinic-brand config). Mentor path only — the support surface sends the session's
// clinical document (prescription / therapy / screening / assessment report) FROM AUMHUM through a
// dedicated report-type-aware flow (sendSupportReportToPatient), not this practitioner-attributed one.
// `deliveryMode` is 'attachment' | 'link' (from the two Send-Prescription buttons); omit to use the
// clinic-brand default.
export async function sendQuickActionPrescription(context, deliveryMode) {
  return sendMentorPrescriptionByContext(toContextPayload(context), deliveryMode)
}

// Lists the patient's prescriptions (newest first) with visit number + event date so the drawer
// can offer a picker. Mentor path only (see sendQuickActionPrescription).
export async function loadQuickActionPrescriptions(context) {
  return getMentorPrescriptionsByContext(toContextPayload(context))
}

// Creates an ad-hoc Razorpay payment link for `amount` (rupees) and sends it to the patient
// (email always; WhatsApp in-window). Returns the short URL for copy/share. `fromName` (support only)
// is the "from" name shown to the patient; mentor sends use the clinic-brand name.
export async function sendQuickActionPaymentLink(context, amount, purpose, fromName) {
  if (isSupportSource(context)) {
    return sendSupportPatientPaymentLink(context?.bookedEventId, amount, purpose, fromName)
  }
  return sendMentorPaymentLinkByContext(toContextPayload(context), amount, purpose)
}

// Bill Patient — create a line-item receipt (generates a PDF), fetch its PDF for preview, and send
// it to the patient (WhatsApp/email). Mentor-only for now.
export async function createQuickActionReceipt(context, input) {
  if (isSupportSource(context)) {
    return createSupportPatientReceipt(context?.bookedEventId, input)
  }
  return createMentorReceiptByContext(toContextPayload(context), input)
}

// receiptId-keyed; the source decides which portal's endpoint owns the receipt.
export async function fetchQuickActionReceiptPdf(context, receiptId) {
  if (isSupportSource(context)) {
    return fetchSupportPatientReceiptPdfBlobUrl(receiptId)
  }
  return fetchMentorReceiptPdfBlobUrl(receiptId)
}

export async function sendQuickActionReceipt(context, receiptId, channels) {
  if (isSupportSource(context)) {
    return sendSupportPatientReceipt(receiptId, channels)
  }
  return sendMentorReceipt(receiptId, channels)
}

// Sends the clinic's Google review link to the patient (WhatsApp/email). Mentor-only for now.
// The clinic owner must have configured a Google review link/Place ID first.
export async function sendQuickActionGoogleReview(context) {
  if (isSupportSource(context)) {
    return sendSupportAgentGoogleReview(toContextPayload(context))
  }
  return sendMentorGoogleReviewByContext(toContextPayload(context))
}
